import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area, CartesianGrid
} from 'recharts';
import api from '../api/axios';

const COLORS = ['#16a34a', '#facc15', '#f59e0b', '#ef4444', '#22c55e'];

const StatCard = ({ icon, label, value, sub, color, change, onClick }) => (
  <div className={`stat-card ${color}`} style={onClick ? { cursor: 'pointer' } : {}} onClick={onClick}>
    <div className="stat-icon">{icon}</div>
    <div className="stat-value">{value}</div>
    <div className="stat-label">{label}</div>
    {sub && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>{sub}</div>}
    {change !== undefined && change !== null && (
      <div className={`stat-change ${parseFloat(change) >= 0 ? 'up' : 'down'}`}>
        {parseFloat(change) >= 0 ? '▲' : '▼'} {Math.abs(parseFloat(change))}% vs last month
      </div>
    )}
  </div>
);

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [finData, setFinData] = useState(null);
  const [pnlTrend, setPnlTrend] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const formatCurrency = (v) => `₹${parseFloat(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

  const fetchDashboard = useCallback(async () => {
    try {
      const [dashRes, finRes, trendRes] = await Promise.all([
        api.get('/analytics/dashboard'),
        api.get('/finance/dashboard').catch(() => ({ data: { data: null } })),
        api.get('/finance/pnl/trend?months=6').catch(() => ({ data: { data: [] } })),
      ]);
      setData(dashRes.data.data);
      setFinData(finRes.data.data);
      setPnlTrend(trendRes.data.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDashboard(); const i = setInterval(fetchDashboard, 120000); return () => clearInterval(i); }, [fetchDashboard]);

  if (loading) return <div className="flex-center" style={{ height: 300 }}><div className="spinner"></div></div>;
  if (!data) return <div className="empty-state"><div className="empty-icon">⚠️</div><p>Failed to load dashboard</p></div>;

  const orders = data?.orders || {};
  const revenue = data?.revenue || {};
  const products = data?.products || {};
  const staff = data?.staff || {};
  const recentOrders = data?.recentOrders || [];
  const dailyRevenue = data?.dailyRevenue || [];
  const topApartments = data?.topApartments || [];
  const ordersByStatus = data?.ordersByStatus || [];

  return (
    <div>
      {/* Stats */}
      <div className="stats-grid">
        <StatCard icon="📦" label="Total Orders" value={orders.total_orders} sub={`${orders.today_orders} today`} color="blue" />
        <StatCard icon="⏳" label="Pending" value={orders.pending_orders} color="yellow" />
        <StatCard icon="✅" label="Delivered" value={orders.delivered_orders} color="green" />
        <StatCard icon="₹" label="Total Revenue" value={formatCurrency(revenue.total_revenue)} sub={`${formatCurrency(revenue.today_revenue)} today`} color="blue" />
        <StatCard icon="📦" label="Products" value={products.total_products} sub={`${products.low_stock} low stock`} color="green" />
        <StatCard icon="👥" label="Active Staff" value={staff.active_staff} sub={`${staff.delivery_staff} delivery`} color="blue" />
      </div>

      {/* P&L Quick Stats (only if finance data loaded) */}
      {finData && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1 }}>
            💰 Financial Overview — This Month
          </div>
          <div className="stats-grid">
            <StatCard
              icon="💰" label="Revenue" color="green"
              value={formatCurrency(finData.thisMonth.revenue)}
              change={finData.revenueChange}
              onClick={() => navigate('/finance')}
            />
            <StatCard
              icon="💸" label="Expenses" color="red"
              value={formatCurrency(finData.thisMonth.expenses)}
              onClick={() => navigate('/finance')}
            />
            <StatCard
              icon={finData.thisMonth.profit >= 0 ? '📈' : '📉'}
              label={finData.thisMonth.profit >= 0 ? 'Net Profit' : 'Net Loss'}
              color={finData.thisMonth.profit >= 0 ? 'green' : 'red'}
              value={`${finData.thisMonth.profit >= 0 ? '+' : '-'}${formatCurrency(Math.abs(finData.thisMonth.profit))}`}
              change={finData.profitChange}
              onClick={() => navigate('/finance')}
            />
          </div>
        </div>
      )}

      {/* Charts row */}
      <div className="charts-grid">
        <div className="card">
          <div className="card-header">
            <span className="card-title">📈 Revenue — Last 7 Days</span>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={dailyRevenue}>
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={d => d?.slice(5)} />
              <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => [`₹${parseFloat(v).toLocaleString('en-IN')}`, 'Revenue']}
                contentStyle={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, color: '#f1f5f9' }} />
              <Line type="monotone" dataKey="revenue" stroke="#16a34a" strokeWidth={2.5} dot={{ fill: '#16a34a', r: 4 }} activeDot={{ r: 6 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="card">
          <div className="card-header"><span className="card-title">🍩 Order Status</span></div>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={ordersByStatus} dataKey="count" nameKey="order_status" cx="50%" cy="50%" outerRadius={80} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false} fontSize={10}>
                {ordersByStatus.map((entry, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, color: '#f1f5f9' }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* P&L Trend Chart */}
      {pnlTrend.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div className="card">
            <div className="card-header">
              <span className="card-title">📊 Profit & Loss Trend — Last 6 Months</span>
              <button className="btn btn-secondary btn-sm" onClick={() => navigate('/finance')}>View Details</button>
            </div>
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={pnlTrend}>
                <defs>
                  <linearGradient id="dashRevGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#16a34a" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#16a34a" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="dashProfGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#facc15" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#facc15" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={v => `₹${(v / 1000).toFixed(0)}k`} />
                <Tooltip
                  formatter={(v, name) => [`₹${parseFloat(v).toLocaleString('en-IN')}`, name]}
                  contentStyle={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, color: '#f1f5f9' }}
                />
                <Area type="monotone" dataKey="revenue" name="Revenue" stroke="#16a34a" fill="url(#dashRevGrad)" strokeWidth={2} />
                <Area type="monotone" dataKey="profit" name="Profit" stroke="#facc15" fill="url(#dashProfGrad)" strokeWidth={2} />
                <Line type="monotone" dataKey="expenses" name="Expenses" stroke="#ef4444" strokeWidth={2} strokeDasharray="5 5" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Bottom row */}
      <div className="resp-grid-2">
        {/* Recent Orders */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">🕐 Recent Orders</span>
            <button className="btn btn-secondary btn-sm" onClick={() => navigate('/orders')}>View All</button>
          </div>
          <div className="table-wrapper">
            <table>
              <thead><tr><th>Customer</th><th>Apartment</th><th>Amount</th><th>Status</th></tr></thead>
              <tbody>
                {recentOrders.map(o => (
                  <tr key={o.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/orders/${o.id}`)}>
                    <td><div style={{ fontWeight: 600 }}>{o.customer_name}</div><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{o.phone}</div></td>
                    <td style={{ fontSize: 12 }}>{o.apartment_name || '—'}</td>
                    <td style={{ fontWeight: 600 }}>₹{parseFloat(o.total_amount).toLocaleString('en-IN')}</td>
                    <td><span className={`badge badge-${o.order_status}`}>{o.order_status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Top Apartments */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">🏢 Top Apartments</span>
            <button className="btn btn-secondary btn-sm" onClick={() => navigate('/apartments')}>View All</button>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={topApartments} layout="vertical">
              <XAxis type="number" tick={{ fontSize: 10, fill: '#94a3b8' }} />
              <YAxis dataKey="apartment_name" type="category" width={100} tick={{ fontSize: 10, fill: '#94a3b8' }} />
              <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, color: '#f1f5f9' }} />
              <Bar dataKey="order_count" fill="#16a34a" radius={[0,4,4,0]} name="Orders" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
